import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { useFilterQueryState } from 'src/components/MainFilterBar/useFilterQueryState';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { DownloadIcon, IconsFill, ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  CommandOptionType,
  Feature,
  ListingClient,
  ListingQuery,
} from 'src/WebApiController';

import { BulkActionItem } from '../types';

export const useExportInventoryAction = ({
  textContentId,
  iconOnlyMode,
  textOnly,
  variant,
  disabled,
  ...props
}: {
  textContentId?: ContentId;
  iconOnlyMode?: boolean;
  textOnly?: boolean;
  variant?: string;
} & Omit<ComponentProps<typeof ButtonWithIcon>, 'icon' | 'onClick'>) => {
  const { eventsTransformed } = useCatalogDataContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const hasExportInventoryFeature = useUserHasFeature(Feature.ExportInventory);
  const { showErrorDialog } = useClientLogContext();
  const { filterQuery } = useFilterQueryContext<ListingQuery>();
  const { filterQueryWithEventIds } = useFilterQueryState<ListingQuery>(
    ActionOutboxEntityType.Listing
  );
  const [isLoading, setIsLoading] = useState(false);
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const isButtonDisabled = isLoading || eventsTransformed == null;

  const onExportButtonClicked = useCallback(async () => {
    setIsLoading(true);
    await tryInvokeApi(
      async () => {
        const result = await new ListingClient(
          activeAccountWebClientConfig
        ).exportListings({
          item1: filterQueryWithEventIds,
          item2: {
            hasAutoPricingFeature,
            // POS-9624: decided server-side from the LastPriceUpdateUser feature
            hasLastPriceUpdateUserFeature: null,
          },
        });

        if (result.data) {
          const url = URL.createObjectURL(result.data);
          const a = document.createElement('a');
          a.setAttribute('href', url);
          a.setAttribute('download', result.fileName ?? '');
          a.click();
          URL.revokeObjectURL(url);
          a.remove();
        }
      },
      (error) => {
        showErrorDialog('ListingClient.exportListings', error, {
          trackErrorData: { filterQuery },
        });
      },
      () => setIsLoading(false)
    );
  }, [
    activeAccountWebClientConfig,
    filterQuery,
    filterQueryWithEventIds,
    hasAutoPricingFeature,
    showErrorDialog,
  ]);

  const iconFill = useMemo(
    () =>
      disabled
        ? IconsFill.textDisabled
        : iconOnlyMode || (variant ?? 'text') !== 'regular'
          ? IconsFill.textBrand
          : IconsFill.textInverted,
    [iconOnlyMode, disabled, variant]
  );

  const openDialogStableRef = useEventCallback(() => {
    isButtonDisabled ? undefined : onExportButtonClicked();
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingExportListing,
        onSelect: openDialogStableRef,
      },
      disabled: !hasExportInventoryFeature,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingExportListing,
        disabled: !hasExportInventoryFeature,
      });
    };
  }, [
    hasExportInventoryFeature,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    return [
      {
        title: textContentId || ContentId.Export,
        disabled: isLoading || disabled,
        onClick: (e: React.MouseEvent) => {
          if (!isButtonDisabled) {
            onExportButtonClicked();
          }
        },
      },
    ];
  }, [
    disabled,
    isButtonDisabled,
    isLoading,
    onExportButtonClicked,
    textContentId,
  ]);

  return {
    dropdown: (
      <PosDropdownItem key="ExportInventory">
        <ButtonWithIcon
          {...props}
          onClick={isButtonDisabled ? undefined : onExportButtonClicked}
          disabled={isLoading || disabled}
          icon={
            !textOnly ? (
              isLoading ? (
                <RotatingWrapper>
                  <ProcessingIcon fill={iconFill} />
                </RotatingWrapper>
              ) : (
                <DownloadIcon withHoverEffect fill={iconFill} />
              )
            ) : null
          }
          iconOnlyMode={iconOnlyMode}
          variant={variant ?? 'text'}
          textContentId={textContentId || ContentId.Export}
        />
      </PosDropdownItem>
    ),
    actionItems,
  };
};
